import { useMemo, useState, useRef, useCallback } from 'react';
import { motion, useInView } from 'framer-motion';
import { ZoomIn, ZoomOut, RotateCcw, Move } from 'lucide-react';
import type { TranscriptSegment, Speaker } from '@/types';
import { formatTimestamp, formatAbsoluteTime } from '@/utils/parser';

interface TimelineProps {
  segments: TranscriptSegment[];
  speakers: Speaker[];
  duration: number;
  recordingDate: Date | string;
  showAbsoluteTime: boolean;
}

export function Timeline({ segments, speakers, duration, recordingDate, showAbsoluteTime }: TimelineProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const isInView = useInView(containerRef, { once: true, margin: '-50px' });
  
  // Zoom and pan state
  const [zoom, setZoom] = useState(1);
  const [panOffset, setPanOffset] = useState(0);
  const [isDragging, setIsDragging] = useState(false);
  const [dragStart, setDragStart] = useState(0);
  const [hoveredSegment, setHoveredSegment] = useState<TranscriptSegment | null>(null);

  const speakerColorMap = useMemo(() => {
    const map = new Map<string, string>();
    speakers.forEach((s) => map.set(s.id, s.color));
    return map;
  }, [speakers]);

  const speakerNameMap = useMemo(() => {
    const map = new Map<string, string>();
    speakers.forEach((s) => map.set(s.id, s.name));
    return map;
  }, [speakers]);
  
  // Helper function to format time based on mode
  const formatTime = useCallback((ms: number) => {
    return showAbsoluteTime 
      ? formatAbsoluteTime(ms, recordingDate)
      : formatTimestamp(ms);
  }, [showAbsoluteTime, recordingDate]);

  // Time markers for the timeline with zoom consideration - ensure proportionate spacing
  const timeMarkers = useMemo(() => {
    const markers: number[] = [];
    const visibleDuration = duration / zoom;
    const startTime = panOffset * duration;
    const endTime = startTime + visibleDuration;
    
    // Calculate appropriate interval based on visible duration
    let interval: number;
    if (visibleDuration > 3600000) { // > 1 hour
      interval = 600000; // 10 minutes
    } else if (visibleDuration > 1800000) { // > 30 min
      interval = 300000; // 5 minutes
    } else if (visibleDuration > 600000) { // > 10 min
      interval = 120000; // 2 minutes
    } else if (visibleDuration > 180000) { // > 3 min
      interval = 60000; // 1 minute
    } else if (visibleDuration > 60000) { // > 1 min
      interval = 30000; // 30 seconds
    } else if (visibleDuration > 30000) { // > 30 sec
      interval = 10000; // 10 seconds
    } else {
      interval = 5000; // 5 seconds
    }
    
    const firstMarker = Math.floor(startTime / interval) * interval;
    for (let t = firstMarker; t <= endTime && t <= duration; t += interval) {
      if (t >= 0) markers.push(t);
    }
    
    // Limit to reasonable number of markers for readability
    return markers.slice(0, 12);
  }, [duration, zoom, panOffset]);

  // Zoom controls
  const handleZoomIn = useCallback(() => {
    setZoom(prev => Math.min(prev * 1.5, 10));
  }, []);

  const handleZoomOut = useCallback(() => {
    setZoom(prev => {
      const newZoom = Math.max(prev / 1.5, 1);
      if (newZoom === 1) setPanOffset(0);
      return newZoom;
    });
  }, []);

  const handleReset = useCallback(() => {
    setZoom(1);
    setPanOffset(0);
  }, []);

  // Pan handlers
  const handleMouseDown = useCallback((e: React.MouseEvent) => {
    if (zoom > 1) {
      setIsDragging(true);
      setDragStart(e.clientX);
    }
  }, [zoom]);

  const handleMouseMove = useCallback((e: React.MouseEvent) => {
    if (isDragging && containerRef.current) {
      const containerWidth = containerRef.current.offsetWidth;
      const delta = (dragStart - e.clientX) / containerWidth / zoom;
      const maxPan = 1 - 1 / zoom;
      setPanOffset(prev => Math.max(0, Math.min(maxPan, prev + delta)));
      setDragStart(e.clientX);
    }
  }, [isDragging, dragStart, zoom]);

  const handleMouseUp = useCallback(() => {
    setIsDragging(false);
  }, []);

  // Calculate visible range
  const visibleStart = panOffset * duration;
  const visibleEnd = visibleStart + duration / zoom;

  // Filter and position segments within visible range
  const getSegmentStyle = useCallback((segment: TranscriptSegment) => {
    const segStart = segment.startTime;
    const segEnd = segment.endTime;
    
    // Check if segment is in visible range
    if (segEnd < visibleStart || segStart > visibleEnd) return null;
    
    const visibleDuration = duration / zoom;
    const left = ((segStart - visibleStart) / visibleDuration) * 100;
    const width = ((segEnd - segStart) / visibleDuration) * 100;
    
    return {
      left: `${Math.max(0, left)}%`,
      width: `${Math.min(100 - Math.max(0, left), Math.max(width, 0.5))}%`,
    };
  }, [visibleStart, visibleEnd, duration, zoom]);

  if (duration === 0) {
    return (
      <div className="h-24 flex items-center justify-center text-slate-500 dark:text-slate-400">
        No timeline data available
      </div>
    );
  }

  return (
    <div ref={containerRef} className="space-y-4">
      {/* Infographic Header with Controls */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <motion.div
            initial={{ scale: 0 }}
            animate={isInView ? { scale: 1 } : {}}
            transition={{ type: 'spring', delay: 0.2 }}
            className="w-8 h-8 bg-gradient-to-br from-sky-100 to-blue-200 dark:from-sky-900 dark:to-blue-800 rounded-lg flex items-center justify-center"
          >
            <Move className="w-4 h-4 text-sky-600 dark:text-sky-400" />
          </motion.div>
          <div>
            <p className="text-sm font-semibold text-slate-700 dark:text-slate-200">
              Interactive Timeline
            </p>
            <p className="text-xs text-slate-500 dark:text-slate-400">
              {zoom > 1 ? `${zoom.toFixed(1)}x zoom • Drag to pan` : 'Use controls to zoom in'}
            </p>
          </div>
        </div>
        
        {/* Zoom Controls */}
        <div className="flex items-center gap-1 bg-slate-100 dark:bg-slate-800 rounded-lg p-1">
          <motion.button
            whileHover={{ scale: 1.1 }}
            whileTap={{ scale: 0.95 }}
            onClick={handleZoomOut}
            disabled={zoom <= 1}
            className="p-2 rounded-md hover:bg-slate-200 dark:hover:bg-slate-700 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
            title="Zoom Out"
          >
            <ZoomOut className="w-4 h-4 text-slate-600 dark:text-slate-400" />
          </motion.button>
          <span className="px-2 text-xs font-medium text-slate-600 dark:text-slate-400 min-w-[3rem] text-center">
            {zoom.toFixed(1)}x
          </span>
          <motion.button
            whileHover={{ scale: 1.1 }}
            whileTap={{ scale: 0.95 }}
            onClick={handleZoomIn}
            disabled={zoom >= 10}
            className="p-2 rounded-md hover:bg-slate-200 dark:hover:bg-slate-700 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
            title="Zoom In"
          >
            <ZoomIn className="w-4 h-4 text-slate-600 dark:text-slate-400" />
          </motion.button>
          <motion.button
            whileHover={{ scale: 1.1 }}
            whileTap={{ scale: 0.95 }}
            onClick={handleReset}
            disabled={zoom === 1}
            className="p-2 rounded-md hover:bg-slate-200 dark:hover:bg-slate-700 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
            title="Reset View"
          >
            <RotateCcw className="w-4 h-4 text-slate-600 dark:text-slate-400" />
          </motion.button>
        </div>
      </div>

      {/* Main Timeline SVG - Infographic Style */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={isInView ? { opacity: 1, y: 0 } : {}}
        transition={{ duration: 0.5, delay: 0.3 }}
        className="bg-gradient-to-br from-slate-50 to-slate-100 dark:from-slate-800 dark:to-slate-900 rounded-2xl p-4 border border-slate-200 dark:border-slate-700"
      >
        <svg 
          viewBox="0 0 800 120" 
          className={`w-full h-auto ${zoom > 1 ? 'cursor-grab' : ''} ${isDragging ? 'cursor-grabbing' : ''}`}
          onMouseDown={handleMouseDown}
          onMouseMove={handleMouseMove}
          onMouseUp={handleMouseUp}
          onMouseLeave={handleMouseUp}
        >
          {/* Background track */}
          <rect x="0" y="40" width="800" height="40" rx="8" fill="#e2e8f0" className="dark:fill-slate-700" />
          
          {/* Animated segments */}
          {segments.map((segment, index) => {
            const style = getSegmentStyle(segment);
            if (!style) return null;
            
            const color = speakerColorMap.get(segment.speakerId) ?? '#6b7280';
            const leftPercent = parseFloat(style.left);
            const widthPercent = parseFloat(style.width);
            const x = (leftPercent / 100) * 800;
            const w = (widthPercent / 100) * 800;
            
            return (
              <motion.g
                key={segment.id}
                initial={{ opacity: 0, scaleX: 0 }}
                animate={isInView ? { opacity: 1, scaleX: 1 } : {}}
                transition={{ duration: 0.4, delay: 0.4 + index * 0.02 }}
                style={{ originX: `${x}px` }}
              >
                <rect
                  x={x}
                  y={42}
                  width={Math.max(w, 4)}
                  height={36}
                  rx={4}
                  fill={color}
                  className="transition-all duration-200 cursor-pointer"
                  style={{ filter: hoveredSegment?.id === segment.id ? 'brightness(1.1)' : undefined }}
                  onMouseEnter={() => setHoveredSegment(segment)}
                  onMouseLeave={() => setHoveredSegment(null)}
                />
              </motion.g>
            );
          })}
          
          {/* Time markers */}
          {timeMarkers.map((t, i) => {
            const visibleDuration = duration / zoom;
            const x = ((t - visibleStart) / visibleDuration) * 800;
            if (x < 0 || x > 800) return null;
            
            return (
              <motion.g
                key={t}
                initial={{ opacity: 0 }}
                animate={isInView ? { opacity: 1 } : {}}
                transition={{ delay: 0.5 + i * 0.1 }}
              >
                <line x1={x} y1={85} x2={x} y2={95} stroke="#94a3b8" strokeWidth={1} />
                <text x={x} y={110} textAnchor="middle" fontSize="10" fill="#64748b" fontFamily="monospace">
                  {formatTime(t)}
                </text>
              </motion.g>
            );
          })}
        </svg>

        {/* Hover tooltip */}
        {hoveredSegment && (
          <motion.div
            initial={{ opacity: 0, y: 5 }}
            animate={{ opacity: 1, y: 0 }}
            className="mt-3 p-3 bg-white dark:bg-slate-800 rounded-xl shadow-lg border border-slate-200 dark:border-slate-700"
          >
            <div className="flex items-center gap-2 mb-1">
              <div
                className="w-3 h-3 rounded-full"
                style={{ backgroundColor: speakerColorMap.get(hoveredSegment.speakerId) }}
              />
              <span className="font-medium text-sm text-slate-900 dark:text-white">
                {speakerNameMap.get(hoveredSegment.speakerId)}
              </span>
              <span className="text-xs text-slate-500 dark:text-slate-400 font-mono">
                {formatTime(hoveredSegment.startTime)} ({Math.round((hoveredSegment.endTime - hoveredSegment.startTime) / 1000)}s)
              </span>
            </div>
            <p className="text-sm text-slate-600 dark:text-slate-300 line-clamp-2">
              {hoveredSegment.text}
            </p>
          </motion.div>
        )}
      </motion.div>

      {/* Speaker Lanes - Infographic Style */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={isInView ? { opacity: 1, y: 0 } : {}}
        transition={{ duration: 0.5, delay: 0.5 }}
        className="bg-gradient-to-r from-purple-50 to-blue-50 dark:from-purple-900/20 dark:to-blue-900/20 rounded-2xl p-4 border border-purple-100 dark:border-purple-800/30"
      >
        <div className="flex items-center gap-2 mb-4">
          <div className="w-6 h-6 bg-purple-100 dark:bg-purple-800/50 rounded-lg flex items-center justify-center">
            <svg viewBox="0 0 24 24" className="w-4 h-4 text-purple-600 dark:text-purple-400" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M4 6h16M4 12h16M4 18h16" strokeLinecap="round" />
            </svg>
          </div>
          <h4 className="text-sm font-semibold text-slate-700 dark:text-slate-200">
            Speaker Lanes
          </h4>
        </div>
        
        <div className="space-y-3">
          {speakers.map((speaker, speakerIndex) => {
            const speakerSegments = segments.filter((s) => s.speakerId === speaker.id);

            return (
              <motion.div
                key={speaker.id}
                initial={{ opacity: 0, x: -20 }}
                animate={isInView ? { opacity: 1, x: 0 } : {}}
                transition={{ duration: 0.4, delay: 0.6 + speakerIndex * 0.1 }}
                className="flex items-center gap-3"
              >
                {/* Speaker label with avatar */}
                <div className="w-28 sm:w-36 flex-shrink-0 flex items-center gap-2">
                  <motion.div
                    initial={{ scale: 0 }}
                    animate={isInView ? { scale: 1 } : {}}
                    transition={{ type: 'spring', delay: 0.7 + speakerIndex * 0.1 }}
                    className="w-6 h-6 rounded-full flex items-center justify-center text-white text-xs font-bold shadow-md"
                    style={{ backgroundColor: speaker.color }}
                  >
                    {speaker.name.charAt(0).toUpperCase()}
                  </motion.div>
                  <span className="text-xs font-medium text-slate-700 dark:text-slate-300 truncate">
                    {speaker.name}
                  </span>
                </div>

                {/* Animated Lane */}
                <div className="flex-1 relative h-8 bg-white/50 dark:bg-slate-800/50 rounded-lg overflow-hidden">
                  {speakerSegments.map((segment, segIndex) => {
                    const style = getSegmentStyle(segment);
                    if (!style) return null;

                    return (
                      <motion.div
                        key={segment.id}
                        initial={{ scaleX: 0 }}
                        animate={isInView ? { scaleX: 1 } : {}}
                        transition={{ duration: 0.3, delay: 0.8 + speakerIndex * 0.1 + segIndex * 0.02 }}
                        className="absolute h-full rounded transition-all hover:brightness-110"
                        style={{
                          left: style.left,
                          width: style.width,
                          backgroundColor: speaker.color,
                          transformOrigin: 'left',
                        }}
                        title={segment.text.slice(0, 100)}
                      />
                    );
                  })}
                </div>
              </motion.div>
            );
          })}
        </div>
      </motion.div>

      {/* Analogy Tip */}
      <motion.div
        initial={{ opacity: 0, y: 10 }}
        animate={isInView ? { opacity: 1, y: 0 } : {}}
        transition={{ delay: 1 }}
        className="bg-amber-50 dark:bg-amber-900/20 rounded-xl p-4 border border-amber-100 dark:border-amber-800/30"
      >
        <div className="flex items-start gap-3">
          <div className="w-8 h-8 bg-amber-100 dark:bg-amber-800/50 rounded-lg flex items-center justify-center flex-shrink-0">
            <svg viewBox="0 0 24 24" className="w-5 h-5 text-amber-600 dark:text-amber-400" fill="currentColor">
              <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-2 15l-5-5 1.41-1.41L10 14.17l7.59-7.59L19 8l-9 9z" />
            </svg>
          </div>
          <div>
            <h5 className="font-semibold text-amber-800 dark:text-amber-200 text-sm mb-1">
              💡 Reading the Timeline
            </h5>
            <p className="text-xs text-amber-700 dark:text-amber-300">
              Each colored block represents when a speaker is talking. Zoom in to see detailed segments, 
              hover over any block to see what was said. The swim lanes below show each speaker's 
              contribution across the entire conversation.
            </p>
          </div>
        </div>
      </motion.div>
    </div>
  );
}
